import { SavingStatus } from "components/Spinners/Saving"

export enum NotificationSettingCategory {
    EMAIL = "EMAIL",
    // SLACK = "SLACK", TODO: uncomment out when we've finalized support 
    SMS = "SMS",
    // WECHAT = "WECHAT", TODO: uncomment out when we've finalized support
    WHATSAPP = "WHATSAPP"
}

export enum NotificationType {
    // AD_HOC = "AD_HOC", uncomment out when implemented
    AGENDA = "AGENDA",
    APPT_BOOKED = "APPT_BOOKED",
    APPT_CANCELLED = "APPT_CANCELLED",
    APPT_COMPLETE = "APPT_COMPLETE",
    APPT_NO_SHOW = "APPT_NO_SHOW",
    APPT_RESCHEDULED = "APPT_RESCHEDULED",
    APPT_SERIES_BOOKED = "APPT_SERIES_BOOKED",
    APPT_UPCOMING = "APPT_UPCOMING",
    // CUSTOM_NOTIFICATION = "CUSTOM_NOTIFICATION", uncomment out when implemented
    MEMBERSHIP_PACKAGE_CANCELLED = "MEMBERSHIP_PACKAGE_CANCELLED",
    MEMBERSHIP_PACKAGE_EXPIRY_UPCOMING = "MEMBERSHIP_PACKAGE_EXPIRY_UPCOMING",
    MEMBERSHIP_PACKAGE_PURCHASED = "MEMBERSHIP_PACKAGE_PURCHASED",
    MEMBERSHIP_PACKAGE_USAGE_WARNING = "MEMBERSHIP_PACKAGE_USAGE_WARNING",
    MEMBERSHIP_RENEWAL_FAILED = "MEMBERSHIP_RENEWAL_FAILED",
    MEMBERSHIP_RENEWAL_RETRIES_MAXED_OUT = "MEMBERSHIP_RENEWAL_RETRIES_MAXED_OUT",
    MEMBERSHIP_RENEWAL_UPCOMING = "MEMBERSHIP_RENEWAL_UPCOMING",
    MEMBERSHIP_RENEWED = "MEMBERSHIP_RENEWED",
    MEMBERSHIP_SUBSCRIPTION_CANCELLED = "MEMBERSHIP_SUBSCRIPTION_CANCELLED",
    MEMBERSHIP_SUBSCRIPTION_PURCHASED = "MEMBERSHIP_SUBSCRIPTION_PURCHASED",
    ORDER_SUCCESS = "ORDER_SUCCESS",
    PAYMENT_FAILED = "PAYMENT_FAILED",
    SUBSCRIPTION_CANCELLED = "SUBSCRIPTION_CANCELLED",
    SUBSCRIPTION_RENEWAL_UPCOMING = "SUBSCRIPTION_RENEWAL_UPCOMING",
    SUBSCRIPTION_RENEWED = "SUBSCRIPTION_RENEWED"
}

export const BEFORE_NOTIF_TYPES: NotificationType[] = [NotificationType.APPT_UPCOMING, NotificationType.MEMBERSHIP_RENEWAL_UPCOMING, NotificationType.SUBSCRIPTION_RENEWAL_UPCOMING, NotificationType.MEMBERSHIP_PACKAGE_EXPIRY_UPCOMING]
export const AFTER_NOTIF_TYPES: NotificationType[] = [NotificationType.APPT_COMPLETE, NotificationType.APPT_NO_SHOW]

export enum NotificationRecipientType {
    CLIENTS = "CLIENTS",
    CLIENTS_AND_INSTRUCTORS = "CLIENTS_AND_INSTRUCTORS",
    CLIENTS_INSTRUCTORS_AND_ADMIN = "CLIENTS_INSTRUCTORS_AND_ADMIN"
}

export type NotificationCategory = {
    name: NotificationCategoryGroupNames
    settings: NotificationSetting[]
}

export type NotificationSettingApi = {
    id: string
    category: NotificationSettingCategory
    type: NotificationType
    recipientType: NotificationRecipientType
    enabled: boolean
    serviceIds: string[]
    categoryIds: string[]
    locationIds: string[]
    couponIds: string
    sendAfterMinutes: number[]
    sendBeforeMinutes: number[]
    sendOnUsagePercentages?: number[]
    templates: NotificationTemplateApi[]
    name: string
}

export type NotificationSetting = {
    id: string
    category_index: number
    category: NotificationSettingCategory
    type: NotificationType
    recipient_type: NotificationRecipientType
    enabled: boolean
    service_ids: string[]
    category_ids: string[]
    location_ids: string[]
    coupon_ids: string
    send_after_minutes: number[]
    send_before_minutes: number[]
    send_on_usage_percentages?: number[]
    templates: NotificationTemplate[]
    status: SavingStatus
    name: string
}

export type NotificationTemplateApi = {
    id?: string
    languageId: string
    messageContent: string
    messageSubject: string
    notificationSettingId: string
}

export type NotificationTemplate = {
    id?: string
    language_id: string
    message_content: string
    message_subject: string
    notification_setting_id: string
}

export enum NotificationCategoryGroupNames {
    BOOKING_AND_APPOINTMENTS = "notifications.booking_and_appointments_emails",
    MEMBERSHIP = "notifications.membership_emails",
    OTHER = "notifications.other_emails"
}

export function getNotificationSettingsCategoryIndexFromType(type: NotificationType) {
    switch (type) {
        case NotificationType.APPT_BOOKED:
        case NotificationType.APPT_CANCELLED:
        case NotificationType.APPT_COMPLETE:
        case NotificationType.APPT_NO_SHOW:
        case NotificationType.APPT_RESCHEDULED:
        case NotificationType.APPT_SERIES_BOOKED:
        case NotificationType.APPT_UPCOMING:
            return 0
            break
        case NotificationType.MEMBERSHIP_PACKAGE_CANCELLED:
        case NotificationType.MEMBERSHIP_PACKAGE_EXPIRY_UPCOMING:
        case NotificationType.MEMBERSHIP_PACKAGE_PURCHASED:
        case NotificationType.MEMBERSHIP_PACKAGE_USAGE_WARNING:
        case NotificationType.MEMBERSHIP_RENEWAL_FAILED:
        case NotificationType.MEMBERSHIP_RENEWAL_RETRIES_MAXED_OUT:
        case NotificationType.MEMBERSHIP_RENEWAL_UPCOMING:
        case NotificationType.MEMBERSHIP_RENEWED:
        case NotificationType.MEMBERSHIP_SUBSCRIPTION_CANCELLED:
        case NotificationType.MEMBERSHIP_SUBSCRIPTION_PURCHASED:
        case NotificationType.SUBSCRIPTION_CANCELLED:
        case NotificationType.SUBSCRIPTION_RENEWAL_UPCOMING:
        case NotificationType.SUBSCRIPTION_RENEWED:
            return 1
            break
        default:
            return 2
            break
    }
}

export function mapNotificationSettings(settings: NotificationSettingApi[], setting_id?: string): NotificationCategory[] {
    let categories: NotificationCategory[] = [
        {
            name: NotificationCategoryGroupNames.BOOKING_AND_APPOINTMENTS,
            settings: []
        },
        {
            name: NotificationCategoryGroupNames.MEMBERSHIP,
            settings: []
        },
        {
            name: NotificationCategoryGroupNames.OTHER,
            settings: []
        }

    ]
    settings.forEach((setting) => {
        let category_index = getNotificationSettingsCategoryIndexFromType(setting.type)
        const mapped = {
            ...setting,
            recipient_type: setting.recipientType,
            service_ids: setting.serviceIds,
            category_ids: setting.categoryIds,
            location_ids: setting.locationIds,
            coupon_ids: setting.couponIds,
            send_after_minutes: setting.sendAfterMinutes,
            send_before_minutes: setting.sendBeforeMinutes,
            send_on_usage_percentages: setting.sendOnUsagePercentages,
            templates: setting?.templates?.map((template) => {
                return ({
                    ...template,
                    language_id: template.languageId,
                    message_content: template.messageContent,
                    message_subject: template.messageSubject,
                    notification_setting_id: template.notificationSettingId
                } as NotificationTemplate)
            }),
            status: setting.id == setting_id ? SavingStatus.SAVED_SUCCESS : SavingStatus.NONE,
            category_index
        } as NotificationSetting
        categories[category_index].settings.push(mapped)
    })

    categories[0].settings = categories[0].settings.sort((a, b) => a.name.localeCompare(b.name))
    categories[1].settings = categories[1].settings.sort((a, b) => a.name.localeCompare(b.name))
    categories[2].settings = categories[2].settings.sort((a, b) => a.name.localeCompare(b.name))
    return categories
}

export function mapSettingToApi(setting: NotificationSetting): NotificationSettingApi {
    return ({
        ...setting,
        recipientType: setting.recipient_type,
        serviceIds: setting.service_ids,
        categoryIds: setting.category_ids,
        locationIds: setting.location_ids,
        couponIds: setting.coupon_ids,
        sendAfterMinutes: setting.send_after_minutes,
        sendBeforeMinutes: setting.send_before_minutes,
        sendOnUsagePercentages: setting.send_on_usage_percentages,
        templates: setting?.templates?.map((template) => {
            return ({
                ...template,
                languageId: template.language_id,
                messageContent: template.message_content,
                messageSubject: template.message_subject,
                notificationSettingId: template.notification_setting_id
            } as NotificationTemplateApi)
        })
    } as NotificationSettingApi)
}